import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import type { RootState } from "../../../../store/redux/store/store";
import { EndOfServiceDetailsInput, EndOfServiceDetailsView, EndOfServiceTypeInput, EndOfServiceTypeView, CompanyGrossPayView } from "../../../../models/payroll";
import { AddEndOfServiceDetail, AddEndOfServiceType, GetEndOfServiceDetails, GetEndOfServiceTypes, UpdateEOSDetail } from "../../../../api/Payroll/payroll";
import { GetCompanyGrossPays } from "../../../../api/Payroll/grossPay";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../../../../components/ui/Accordion";
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle } from "../../../../components/ui/Dialog";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../../../components/ui/Table";
import Select from "../../../../components/ui/Select";
import Input from "../../../../components/ui/Input";
import Button from "../../../../components/ui/Button";
import { Spinner } from "../../../../components/ui/Spinner";
import { StatusBadge } from "../../../../components/ui/StatusBadge";
import AppIcon from "../../../../components/AppIcon";
import { PageLayout } from "../../../../components/ui/PageLayout";
import { UserMinus } from "lucide-react";
import { useToast, ToastRenderer } from "../../../../hooks/useToast";
import { useComplianceCountry } from "../../../../hooks/useComplianceCountry";
import { cn } from "../../../../utils/cn";

const EndOfService: React.FC = () => {
  const { t } = useTranslation();
  const company = useSelector((state: RootState) => state.user.company);
  const complianceCountry = useComplianceCountry();
  const { toast, showToast, dismissToast } = useToast();

  const [types, setTypes] = useState<EndOfServiceTypeView[]>([]);
  const [details, setDetails] = useState<EndOfServiceDetailsView[]>([]);
  const [salaryComponents, setSalaryComponents] = useState<CompanyGrossPayView[]>([]);
  const [loading, setLoading] = useState(false);

  // Dialog states
  const [openTypeDialog, setOpenTypeDialog] = useState(false);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [openEditDetailDialog, setOpenEditDetailDialog] = useState(false);

  // Form states
  const [newType, setNewType] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [detailForm, setDetailForm] = useState<{ typeId: number | null; years: string; salaryComponentId: number | null; multiplier: string }>({ typeId: null, years: '', salaryComponentId: null, multiplier: '' });
  const [editDetailForm, setEditDetailForm] = useState<{ id: number | null; typeId: number | null; years: string; salaryComponentId: number | null; multiplier: string }>({ id: null, typeId: null, years: '', salaryComponentId: null, multiplier: '' });
  const [yearDuplicateError, setYearDuplicateError] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [typesResp, detailsResp, compsResp] = await Promise.all([
        GetEndOfServiceTypes(),
        GetEndOfServiceDetails(),
        GetCompanyGrossPays(),
      ]);
      setTypes(typesResp.payload || []);
      setDetails(detailsResp.payload || []);
      setSalaryComponents(compsResp.payload || []);
    } catch (err) {
      console.log(err);
      showToast(t('Failed to load end of service configuration'), 'error');
    } finally {
      setLoading(false);
    }
  }, [t, showToast]);

  useEffect(() => { loadData(); }, [loadData]);

  const resetTypeForm = () => { setNewType(''); setNewDescription(''); };
  const resetDetailForm = () => { setDetailForm({ typeId: null, years: '', salaryComponentId: null, multiplier: '' }); setYearDuplicateError(false); };

  const handleAddType = async () => {
    if (!newType.trim()) { showToast(t('Type name is required'), 'error'); return; }
    setLoading(true);
    try {
      const input: EndOfServiceTypeInput = { type: newType.trim(), description: newDescription.trim() || '' };
      await AddEndOfServiceType(input);
      showToast(t('End of service type added'), 'success');
      setOpenTypeDialog(false);
      resetTypeForm();
      loadData();
    } catch (err) {
      console.log(err);
      showToast(t('Failed to add type'), 'error');
    } finally { setLoading(false); }
  };

  const isDuplicateYear = (typeId: number, years: number) => {
    return details.some(d => d.endOfServiceId === typeId && d.years === years);
  };

  const handleAddDetail = async () => {
    const { typeId, years, salaryComponentId, multiplier } = detailForm;
    if (typeId == null || !years || salaryComponentId == null || !multiplier) { showToast(t('All fields are required'), 'error'); return; }
    const yearsNum = Number(years), multNum = Number(multiplier);
    if (Number.isNaN(yearsNum) || yearsNum < 0) { showToast(t('Years must be a non-negative number'), 'error'); return; }
    if (Number.isNaN(multNum) || multNum <= 0) { showToast(t('Multiplier must be > 0'), 'error'); return; }
    if (isDuplicateYear(typeId, yearsNum)) { setYearDuplicateError(true); return; }
    setLoading(true);
    try {
      const input: EndOfServiceDetailsInput = { typeId, years: yearsNum, salaryComponentId, multiplier: multNum } as any;
      await AddEndOfServiceDetail(input);
      showToast(t('Detail added'), 'success');
      setOpenDetailDialog(false);
      resetDetailForm();
      loadData();
    } catch (err: any) {
      if (err?.response?.status === 406) { setYearDuplicateError(true); } else { showToast(t('Failed to add detail'), 'error'); }
    } finally { setLoading(false); }
  };

  const openEditDetail = (detail: EndOfServiceDetailsView, grouping: EndOfServiceDetailsView[]) => {
    setEditDetailForm({
      id: detail.id,
      typeId: detail.endOfServiceId,
      years: String(detail.years),
      salaryComponentId: detail.salaryComponentId,
      multiplier: String(detail.multiplier)
    });
    setOpenEditDetailDialog(true);
  };

  const handleEditDetail = async () => {
    const { id, typeId, years, salaryComponentId, multiplier } = editDetailForm;
    if (id == null || typeId == null) { showToast(t('Invalid edit state'), 'error'); return; }
    const yearsNum = Number(years), multNum = Number(multiplier);
    if (Number.isNaN(yearsNum) || yearsNum < 0 || Number.isNaN(multNum) || multNum <= 0) { showToast(t('Invalid numeric values'), 'error'); return; }
    // Allow same year for the same record being edited
    if (details.filter(d => d.id !== id).some(d => d.endOfServiceId === typeId && d.years === yearsNum)) { setYearDuplicateError(true); return; }
    setLoading(true);
    try {
      const input: EndOfServiceDetailsInput = { typeId, years: yearsNum, salaryComponentId, multiplier: multNum } as any;
      await UpdateEOSDetail(input, id as any);
      showToast(t('Detail updated'), 'success');
      setOpenEditDetailDialog(false);
      loadData();
    } catch (err) {
      console.log(err);
      showToast(t('Failed to update detail'), 'error');
    } finally { setLoading(false); }
  };

  const typeOptions = types.map(x => ({ label: x.type as string, value: x.id }));
  const salaryComponentOptions = salaryComponents.map(x => ({ label: x.name as string, value: x.id }));

  if (complianceCountry !== 'UAE') {
    return (
      <div className="relative flex flex-col flex-1 p-5 gap-6">
        <h2 className="text-lg font-semibold text-muted-foreground">{t('END OF SERVICE CONFIGURATION')}</h2>
        <div className="flex flex-col items-center justify-center gap-4 rounded-xl border border-border bg-card py-12 px-6 text-center">
          <AppIcon name="Globe" size={48} className="text-muted-foreground" />
          <div>
            <h3 className="text-lg font-semibold text-foreground">{t('End of Service configuration is for UAE only')}</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('The service-year multipliers configured here are scoped to UAE labour law. Support for your country will arrive in a future release.')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <PageLayout
      title={t('END OF SERVICE CONFIGURATION')}
      icon={UserMinus}
      accentColor="emerald"
      headerActions={(
        <div className="flex gap-3 flex-wrap rtl:space-x-reverse">
          <Button variant="outline" iconName="PlusCircle" onClick={() => { resetTypeForm(); setOpenTypeDialog(true); }} data-testid="add-type-btn">{t('ADD END OF SERVICE TYPE')}</Button>
          <Button variant="outline" iconName="PlusCircle" onClick={() => { resetDetailForm(); setOpenDetailDialog(true); }}>{t('ADD END OF SERVICE DETAIL')}</Button>
        </div>
      )}
    >
      <div className="relative flex flex-col flex-1 gap-6">
      <div className="flex flex-col gap-4">
        {types.length === 0 && !loading && (
          <div className="border border-border rounded-xl p-8 text-center bg-card">
            <p className="font-medium text-foreground">{t('No end of service types found')}</p>
            <p className="text-sm text-muted-foreground mt-1">{t('Click the button above to add your first type')}</p>
          </div>
        )}

        <Accordion type="multiple" data-testid="settings-table">
          {types.map(type => {
            const typeDetails = details.filter(d => d.endOfServiceId === type.id).sort((a, b) => a.years - b.years);
            return (
              <AccordionItem key={type.id} value={String(type.id)}>
                <AccordionTrigger>
                  <div className="flex items-center justify-between w-full pe-6">
                    <div className="flex flex-col gap-1 text-start">
                      <span className="text-base font-semibold text-primary">{type.type}</span>
                      {type.description && <span className="text-xs text-muted-foreground">{type.description}</span>}
                    </div>
                    <StatusBadge variant={typeDetails.length ? 'indigo' : 'neutral'} size="sm">
                      {`${typeDetails.length} ${t('rules')}`}
                    </StatusBadge>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <Table aria-label={t('End of Service Rules Table') as string}>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('Years')}</TableHead>
                        <TableHead>{t('Salary Type')}</TableHead>
                        <TableHead>{t('Multiplier(Days)')}</TableHead>
                        <TableHead className="w-[60px]">{t('Edit')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {typeDetails.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4}>
                            <div className="flex items-center justify-center py-6 text-muted-foreground text-center">
                              <span className="text-sm">{t('No rules for this type yet')}</span>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                      {typeDetails.map((detail, idx, arr) => (
                        <TableRow key={detail.id}>
                          <TableCell>
                            {arr[idx + 1] ? (
                              <>{t('From')} {idx === 0 ? 0 : arr[idx - 1].years} {t('years to')} {detail.years} {t('years')}</>
                            ) : (
                              <>{detail.years} {t('years and above')}</>
                            )}
                          </TableCell>
                          <TableCell>{detail.salaryComponent?.name}</TableCell>
                          <TableCell>{detail.multiplier}</TableCell>
                          <TableCell className="w-[60px]">
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={t('Edit')}
                              onClick={() => openEditDetail(detail, arr)}
                            >
                              <AppIcon name="Pencil" size={16} />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>

      {/* Add Type Dialog */}
      <Dialog open={openTypeDialog} onOpenChange={setOpenTypeDialog}>
        <DialogContent size="sm" data-testid="settings-form-dialog">
          <DialogHeader>
            <DialogTitle>{t('ADD NEW END OF SERVICE TYPE')}</DialogTitle>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-4">
            <Input
              label={t('End Of Service Type')}
              value={newType}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewType(e.target.value)}
              required
            />
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground">{t('Description')}</label>
              <textarea
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary min-h-[60px]"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                rows={2}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenTypeDialog(false)}>{t('Cancel')}</Button>
            <Button onClick={handleAddType} data-testid="save-btn">{t('Submit')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Detail Dialog */}
      <Dialog open={openDetailDialog} onOpenChange={setOpenDetailDialog}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>{t('ADD NEW END OF SERVICE DETAILS')}</DialogTitle>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-4">
            {yearDuplicateError && (
              // TODO(batch5): Replace with shared Alert component
              <div className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                <span>{t('Year selected should be unique')}</span>
                <button className="ms-auto hover:opacity-80" onClick={() => setYearDuplicateError(false)}>
                  <AppIcon name="X" size={14} />
                </button>
              </div>
            )}
            <Select
              label={t('End of service Types')}
              options={typeOptions}
              value={detailForm.typeId}
              onChange={(v: number) => setDetailForm(f => ({ ...f, typeId: v ?? null }))}
              searchable
              placeholder={t('Select type')}
            />
            <Input
              label={t('Years')}
              type="number"
              value={detailForm.years}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDetailForm(f => ({ ...f, years: e.target.value }))}
            />
            <Select
              label={t('Salary component Types')}
              options={salaryComponentOptions}
              value={detailForm.salaryComponentId}
              onChange={(v: number) => setDetailForm(f => ({ ...f, salaryComponentId: v ?? null }))}
              searchable
              placeholder={t('Select component')}
            />
            <Input
              label={t('Multiplier(Days)')}
              type="number"
              value={detailForm.multiplier}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDetailForm(f => ({ ...f, multiplier: e.target.value }))}
            />
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDetailDialog(false)}>{t('Cancel')}</Button>
            <Button onClick={handleAddDetail}>{t('Submit')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Detail Dialog */}
      <Dialog open={openEditDetailDialog} onOpenChange={setOpenEditDetailDialog}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>{t('EDIT END OF SERVICE DETAIL')}</DialogTitle>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-4">
            {yearDuplicateError && (
              // TODO(batch5): Replace with shared Alert component
              <div className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                <span>{t('Year selected should be unique')}</span>
                <button className="ms-auto hover:opacity-80" onClick={() => setYearDuplicateError(false)}>
                  <AppIcon name="X" size={14} />
                </button>
              </div>
            )}
            <Select
              label={t('End of service Types')}
              options={typeOptions}
              value={editDetailForm.typeId}
              onChange={(v: number) => setEditDetailForm(f => ({ ...f, typeId: v ?? null }))}
              searchable
              placeholder={t('Select type')}
            />
            <Input
              label={t('Years')}
              type="number"
              value={editDetailForm.years}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditDetailForm(f => ({ ...f, years: e.target.value }))}
            />
            <Select
              label={t('Salary component Types')}
              options={salaryComponentOptions}
              value={editDetailForm.salaryComponentId}
              onChange={(v: number) => setEditDetailForm(f => ({ ...f, salaryComponentId: v ?? null }))}
              searchable
              placeholder={t('Select component')}
            />
            <Input
              label={t('Multiplier(Days)')}
              type="number"
              value={editDetailForm.multiplier}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditDetailForm(f => ({ ...f, multiplier: e.target.value }))}
            />
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenEditDetailDialog(false)}>{t('Cancel')}</Button>
            <Button onClick={handleEditDetail}>{t('Submit')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-card/55">
          <Spinner size="lg" className="text-primary" />
        </div>
      )}

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
      </div>
    </PageLayout>
  );
};

export default EndOfService;
